import React, { useMemo } from "react";

import { Grid, Tooltip } from "@mui/material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { Destination } from "../../../../models/destination";
import { JobMode } from "../../../../models/job.mode";
import { Location } from "../../../../models/location";
import { Material } from "../../../../models/material";
import { RoomJob } from "../../../../models/sales";
import { useAppState } from "../../../../context/StateContext";
import { DESTINATION } from "../../../../util/enums";
import groupBy from "../../../../util/groupBy";
import { formatCurrency, getMaterialsPrice } from "../../../../util/prices";
import SkeletonComponent from "../../Skeleton";
import useStyles from "./styles";

interface EstimateMaterialSection {
  inventory: RoomJob[];
  destinationList: Destination[];
  destinationAddresses: Location[];
  modeDestinationList?: JobMode[];
  loading?: boolean;
  isOnViewMode?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
const EstimateMaterialSection: React.FC<EstimateMaterialSection> = ({
  inventory: inventoryList,
  destinationAddresses,
  modeDestinationList,
  destinationList,
  isOnViewMode = false,
  loading,
}) => {
  const classes = useStyles(isOnViewMode)();
  const { t } = useTranslation();
  const { currentUser } = useAppState();

  const materialsIdName = useMemo(() => {
    const materialIdName: { [x: string]: string } = {};

    inventoryList.forEach((inventory) => {
      inventory?.materials?.forEach((material) => {
        if (!materialIdName[String(material?.packingMaterialId)]) {
          materialIdName[String(material?.packingMaterialId)] = material?.serviceBooking?.name;
        }
      });
    });

    return materialIdName;
  }, [inventoryList]);

  const addressDestinationMaterials = useMemo(() => {
    const destinationsId = destinationAddresses.map((address) => address.id);
    const destinationMaterials: { [x: string]: Material[] } = {};

    destinationsId.forEach((destinationId) => {
      if (destinationId) {
        const materials: Material[] = [];

        inventoryList?.forEach((inventory) => {
          inventory?.materials?.forEach((material) => {
            if (
              material &&
              material.destinationResidenceId &&
              material.destinationResidenceId === destinationId
            ) {
              materials.push(material);
            }
          });
        });
        if (materials && materials.length > 0) {
          destinationMaterials[`id-${destinationId.toString()}`] = materials;
        }
      }
    });

    return destinationMaterials;
  }, [destinationAddresses, inventoryList]);

  const customDestinationMaterials = useMemo(() => {
    const destinationsId = destinationList
      .filter((destination) => !destination.name?.includes(DESTINATION.NOT_GOING))
      .map((destination) => destination.id);
    const destinationMaterials: { [x: string]: Material[] } = {};

    destinationsId.forEach((destinationId) => {
      if (destinationId) {
        const materials: Material[] = [];

        inventoryList?.forEach((inventory) => {
          inventory?.materials?.forEach((material) => {
            if (material && material.destinationId && material.destinationId === destinationId) {
              materials.push(material);
            }
          });
        });
        if (materials && materials.length > 0) {
          destinationMaterials[`id-${destinationId.toString()}`] = materials;
        }
      }
    });

    return destinationMaterials;
  }, [destinationList, inventoryList]);

  const customModeDestinationMaterials = useMemo(() => {
    const modeDestinationsId = modeDestinationList
      ?.filter((modeDestination) => modeDestination.isDestination)
      .map((modeDestination) => modeDestination.id);

    const modeDestinationMaterials: { [x: string]: Material[] } = {};

    modeDestinationsId?.forEach((modeDestinationId) => {
      if (modeDestinationId) {
        const materials: Material[] = [];

        inventoryList?.forEach((inventory) => {
          inventory?.materials?.forEach((material) => {
            if (
              material &&
              material.modeDestinationId &&
              material.modeDestinationId === modeDestinationId
            ) {
              materials.push(material);
            }
          });
        });
        if (materials && materials.length > 0) {
          modeDestinationMaterials[`id-${modeDestinationId.toString()}`] = materials;
        }
      }
    });

    return modeDestinationMaterials;
  }, [modeDestinationList, inventoryList]);

  const renderMaterialLine = (name: string, quantity: number, price: number) => {
    return (
      <Grid container direction="row" className={classes.boxLineRow}>
        <Grid item className={classnames(classes.boxLineColumn, classes.width60)}>
          <Tooltip title={name} placement="top">
            <span>{name}</span>
          </Tooltip>
        </Grid>
        <Grid item className={classnames(classes.boxLineColumn, classes.width20)}>
          <span>{quantity}</span>
        </Grid>
        <Grid item className={classnames(classes.boxLineColumn, classes.width20)}>
          <span>{formatCurrency(price, currentUser?.company?.currency)}</span>
        </Grid>
      </Grid>
    );
  };

  const renderMaterialTableHeader = () => {
    return (
      <Grid container direction="row" className={classes.boxColumnsTitle}>
        <Grid item className={classnames(classes.boxColumnTitle, classes.width60)}>
          <span>{t("material")}</span>
        </Grid>
        <Grid item className={classnames(classes.boxColumnTitle, classes.width20)}>
          <span>{t("qty")}</span>
        </Grid>
        <Grid item className={classnames(classes.boxColumnTitle, classes.width20)}>
          <span>{t("price")}</span>
        </Grid>
      </Grid>
    );
  };

  const renderMaterialTableFooter = (totalQuantity: number, totalPrice: number) => {
    return (
      <Grid container direction="row" className={classes.boxFooter}>
        <Grid item className={classnames(classes.boxFooterColumnTitle, classes.width60)}>
          <span>Total</span>
        </Grid>
        <Grid item className={classnames(classes.boxFooterColumnTitle, classes.width20)}>
          <span>{totalQuantity}</span>
        </Grid>
        <Grid item className={classnames(classes.boxFooterColumnTitle, classes.width20)}>
          <span>{formatCurrency(totalPrice, currentUser?.company?.currency)}</span>
        </Grid>
      </Grid>
    );
  };

  const renderMaterialBox = (
    title: string,
    materials: Material[],
    subTitle = "\u200C",
    color?: string
  ) => {
    const materialsGrouppedById = groupBy(materials, "packingMaterialId");
    const materialKeys = Object.keys(materialsGrouppedById) || [];

    const { price: totalPrice, quantity: totalQuantity } = getMaterialsPrice(materials);

    return (
      <Grid
        data-cy={`estimate-materials-section-${title}`}
        container
        direction="column"
        className={classes.materialBox}
      >
        <Grid container direction="row" className={classes.boxHeader}>
          <Grid item>
            {title && (
              <Grid item className={classes.materialTitle}>
                <span style={{ color }}>{title}</span>
              </Grid>
            )}
            {subTitle && (
              <Grid item>
                <span className={classes.materialSubTitle}>{subTitle}</span>
              </Grid>
            )}
          </Grid>
        </Grid>

        {renderMaterialTableHeader()}

        <Grid container className={classes.boxContent}>
          {materialKeys.map((packingMaterialId) => {
            if (!materialsGrouppedById[String(packingMaterialId)]) return null;
            const materials = materialsGrouppedById[String(packingMaterialId)];
            const { price, quantity } = getMaterialsPrice(materials);

            return renderMaterialLine(materialsIdName[String(packingMaterialId)], quantity, price);
          })}
        </Grid>

        {renderMaterialTableFooter(totalQuantity, totalPrice)}
      </Grid>
    );
  };

  const renderMaterialSection = () => {
    return (
      <>
        {Object.keys(addressDestinationMaterials).map((addressId) => {
          const address = destinationAddresses.find(
            (address) => String(address.id) === String(addressId?.replace("id-", ""))
          );
          const materials = addressDestinationMaterials[addressId];

          if (!address || !materials) return null;

          return renderMaterialBox(t("residence"), materials, address.longAddress || "");
        })}

        {Object.keys(customDestinationMaterials).map((destinationId) => {
          const destination = destinationList.find(
            (destination) => String(destination.id) === String(destinationId?.replace("id-", ""))
          );
          const materials = customDestinationMaterials[destinationId];

          if (!destination || !materials) return null;

          return renderMaterialBox(
            destination.name,
            materials,
            destination.name,
            destination.color
          );
        })}

        {Object.keys(customModeDestinationMaterials).map((modeDestinationId) => {
          const modeDestination = modeDestinationList?.find(
            (modeDestination) =>
              String(modeDestination.id) === String(modeDestinationId?.replace("id-", ""))
          );
          const materials = customModeDestinationMaterials[modeDestinationId];

          if (!modeDestination || !materials) return null;

          return renderMaterialBox(modeDestination.name, materials, modeDestination.name);
        })}
      </>
    );
  };

  return (
    <Grid container direction="column" data-cy="estimate-materials">
      <Grid item className={classes.subTitle}>
        <span data-cy="estimate-materials-title">{t("materials")}</span>
      </Grid>
      {Object.keys(addressDestinationMaterials).length <= 0 &&
        Object.keys(customDestinationMaterials).length <= 0 &&
        !loading && (
          <Grid item className={classes.empty}>
            <span data-cy="estimate-materials-empty">{t("youHavenTAddedAnyMaterialYet")}</span>
          </Grid>
        )}
      <div className={classes.materialSection} data-cy="estimate-materials-section">
        <SkeletonComponent isLoading={!!loading}>{renderMaterialSection()}</SkeletonComponent>
      </div>
    </Grid>
  );
};

export default React.memo(EstimateMaterialSection);
